import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Globe, Send } from 'lucide-react';
import { SEO } from '../components/shared/SEO';

interface Profile {
  name: string;
  title: string;
  bio: string;
  email?: string;
  location?: string;
  website?: string;
}

const About: React.FC = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch('/api/blog-admin/profile');
        const data = await res.json();
        if (data.success) {
          setProfile(data.data);
        }
      } catch (error) {
        console.error('获取个人资料失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const subject = encodeURIComponent(`来自 ${formData.name || '访客'} 的留言`);
    const body = encodeURIComponent(
      `姓名: ${formData.name}\n邮箱: ${formData.email}\n\n内容:\n${formData.message}`,
    );

    window.location.href = `mailto:${profile?.email || 'lighting@example.com'}?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 sm:py-12 px-3 sm:px-4">
      <SEO 
        title="关于与联系"
        description="了解更多关于我的个人信息、职业发展以及随时与我取得联系。"
      />
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 md:p-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">关于与联系</h1>
          <p className="text-gray-600 dark:text-gray-300 leading-6 sm:leading-7">
            我是 {profile?.name || 'LIGHTING'}，{profile?.title || '软件工程师'}。
            这里会持续记录工作、学习、生活与阶段性思考。
            如果你有合作想法、内容交流，或者只是想打个招呼，欢迎直接联系我。
          </p>
        </motion.section>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 md:p-8"
          >
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-5">联系方式</h2>
            <div className="space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-500" />
                <a className="hover:text-blue-600" href={`mailto:${profile?.email || 'lighting@example.com'}`}>
                  {profile?.email || 'lighting@example.com'}
                </a>
              </div>
              {profile?.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span>{profile.location}</span>
                </div>
              )}
              {profile?.website && (
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-500" />
                  <a href={profile.website} target="_blank" rel="noreferrer" className="hover:text-blue-600">
                    {profile.website}
                  </a>
                </div>
              )}
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-6 md:p-8"
          >
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-5">快速留言</h2>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <input
                type="text"
                placeholder="你的姓名"
                value={formData.name}
                onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent text-gray-900 dark:text-white text-sm sm:text-base"
              />
              <input
                type="email"
                placeholder="你的邮箱"
                value={formData.email}
                onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent text-gray-900 dark:text-white text-sm sm:text-base"
              />
              <textarea
                required
                rows={4}
                placeholder="想聊点什么..."
                value={formData.message}
                onChange={(event) => setFormData({ ...formData, message: event.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent text-gray-900 dark:text-white resize-none text-sm sm:text-base"
              />
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm sm:text-base"
              >
                <Send className="w-4 h-4" />
                发送邮件
              </button>
            </form>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default About;
